/**
 * How big an avatar's SOURCE image has to be, given how big it is drawn.
 *
 * `ProfileImage` is handed a Tailwind size (`"w-16 h-16"`) and used at seven or
 * eight different sizes across the app. It used to pick a Gravatar `s=` by
 * substring-matching that string - which asked for a 100px image by default, so
 * the 64px profile header (128 real pixels on a retina screen) was fed a source
 * with barely three quarters of the pixels it needed and the browser had to
 * invent the rest. `'w-8'` also matched `'w-80'`, so the largest avatars in the
 * app quietly got the smallest source of all.
 *
 * These are pure functions so the mapping can be tested without a DOM, and so
 * every consumer of an avatar URL derives its size the same way.
 */

/** Tailwind's spacing unit: `w-16` is 16 × 0.25rem = 64px. */
const TAILWIND_UNIT_PX = 4;

/** The `h-7 w-7` the component falls back to when given no size. */
export const DEFAULT_AVATAR_PX = 28;

/**
 * Source widths we are willing to ask for.
 *
 * A ladder rather than the exact number: Gravatar and the blob CDN cache per
 * URL, and a continuum of sizes would mean a near-unique URL per component
 * instance. Eight rungs cover every avatar in the app.
 */
export const AVATAR_SOURCE_LADDER = [64, 96, 128, 160, 192, 256, 320, 384, 512];

/**
 * Assume a retina screen even when we cannot see one.
 *
 * A 1× source that lands on a 2× display is the exact defect being fixed here,
 * and it is the case we cannot detect server-side or in a test environment.
 * Over-fetching an avatar costs a few KB; under-fetching it is visible.
 */
const MIN_PIXEL_RATIO = 2;

/** Requesting only exactly enough leaves nothing for browser zoom or a scaled container. */
const OVERSAMPLE = 1.25;

/**
 * The drawn width in CSS pixels for a Tailwind size string.
 *
 * Reads `w-*` (falling back to `h-*`) with a word boundary, so `w-80` is 320 and
 * not a match for `w-8`. Understands arbitrary values - `w-[72px]`, `w-[4.5rem]`
 * - because several call sites use them.
 */
export const avatarPixelSize = (size?: string | null): number => {
  if (!size) return DEFAULT_AVATAR_PX;

  const arbitrary = size.match(/\b[wh]-\[(\d+(?:\.\d+)?)(px|rem)?\]/);
  if (arbitrary) {
    const value = parseFloat(arbitrary[1]);
    return arbitrary[2] === 'rem' ? value * 16 : value;
  }

  const scale = size.match(/\bw-(\d+(?:\.\d+)?)\b/) || size.match(/\bh-(\d+(?:\.\d+)?)\b/);
  if (scale) return parseFloat(scale[1]) * TAILWIND_UNIT_PX;

  return DEFAULT_AVATAR_PX;
};

/** The device pixel ratio to plan for, floored at retina and capped so 3× phones don't ask for 1536px. */
export const effectivePixelRatio = (ratio?: number): number => {
  const value = typeof ratio === 'number' && Number.isFinite(ratio) && ratio > 0 ? ratio : 1;
  return Math.min(3, Math.max(MIN_PIXEL_RATIO, value));
};

/**
 * The source width to request for an avatar drawn at `size`.
 *
 * `w-16 h-16` (the profile header) on a retina screen → 64 × 2 × 1.25 = 160.
 */
export const avatarSourcePx = (size?: string | null, ratio?: number): number => {
  const needed = avatarPixelSize(size) * effectivePixelRatio(ratio) * OVERSAMPLE;
  return AVATAR_SOURCE_LADDER.find((rung) => rung >= needed) ?? AVATAR_SOURCE_LADDER[AVATAR_SOURCE_LADDER.length - 1];
};

/**
 * `d=404` - NOT `d=mp`. With `d=mp` Gravatar always answers 200 with its grey
 * "mystery person" silhouette, so `onError` never fires and the third link in
 * the chain (initials) is unreachable: every user without a Gravatar renders as
 * an anonymous grey disc. `d=404` makes Gravatar 404 for an unknown email,
 * which trips the error handler and lands on the initials avatar - while a user
 * who DOES have a Gravatar still gets it.
 */
export const gravatarUrl = (emailHash: string, sourcePx: number): string =>
  `https://www.gravatar.com/avatar/${emailHash}?s=${sourcePx}&d=404&r=g`;

/**
 * Ask the profile-picture endpoint for a variant at least `sourcePx` wide.
 *
 * The endpoint serves a stored render whose default is sized for a list row, so
 * the header was upscaling a thumbnail. The hint is a query parameter, which an
 * endpoint that does not implement it ignores - and `ProfileImage` retries once
 * without it if a sized request fails, so this can never be the reason an avatar
 * disappears.
 */
export const sizedPictureUrl = (url: string, sourcePx: number): string => {
  if (!url) return url;
  if (/[?&](size|width)=/.test(url)) return url;
  return `${url}${url.includes('?') ? '&' : '?'}size=${sourcePx}`;
};
